import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tooltip
} from '@chakra-ui/react';
import { faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonIcon,
  Checkbox,
  OutlineCard,
  Switch
} from '@morressier/alembic';
import React from 'react';

import { FieldLabel } from 'src/components/Field';

import { OtherRoleEntry, COCRecommendationLockReason } from './types';

interface OtherRolesSectionProps {
  roles: OtherRoleEntry[];
  onAdd: () => void;
  onEdit: (entry: OtherRoleEntry) => void;
  onDelete: (entry: OtherRoleEntry) => void;
  isCOCRecommendationRequired?: boolean;
  onToggleRecommendationRequired: (checked: boolean) => void;
  /**
   * Set when the toggle should be frozen; also drives the tooltip explaining
   * why. Ignored while no other roles are configured.
   */
  recommendationLockReason?: COCRecommendationLockReason;
}

const LOCK_REASON_MESSAGES: Record<COCRecommendationLockReason, string> = {
  journalActive:
    'Recommendation can only be deactivated when the journal is inactive.',
  hasSubmissions:
    'Recommendation can only be deactivated when the journal has no submissions.'
};

const OtherRolesSection: React.FC<OtherRolesSectionProps> = ({
  roles,
  onAdd,
  onEdit,
  onDelete,
  isCOCRecommendationRequired,
  onToggleRecommendationRequired,
  recommendationLockReason
}) => {
  /**
   * The toggle only matters once other roles exist — with none configured there
   * is nothing to gate, so it stays freely editable even on a live journal.
   */
  const isRecommendationLocked = roles.length > 0 && !!recommendationLockReason;

  return (
    <Flex flexDirection={'column'} gap={4}>
      {/* Header */}
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Text textStyle={'headingSection'}>Other Roles</Text>
        <Button
          variant="secondary"
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          onClick={onAdd}
        >
          Add Other Role
        </Button>
      </Flex>

      {/* Recommendation requirement toggle */}
      <Flex alignItems="center" justifyContent="space-between">
        <FieldLabel
          label="Enable decision Recommendations"
          subText="Allow users in these roles to submit decision recommendations."
          isRequired={false}
        />
        <Tooltip
          shouldWrapChildren
          isDisabled={!isRecommendationLocked}
          hasArrow
          label={
            recommendationLockReason
              ? LOCK_REASON_MESSAGES[recommendationLockReason]
              : ''
          }
          fontSize="md"
          placement="left"
        >
          <Switch
            isChecked={isCOCRecommendationRequired}
            isDisabled={isRecommendationLocked}
            onChange={event =>
              onToggleRecommendationRequired(event.target.checked)
            }
          />
        </Tooltip>
      </Flex>

      {/* Table or empty state */}
      {roles.length > 0 ? (
        <Box overflowX="auto" width="100%">
          <Table>
            <Thead>
              <Tr
                data-testId="otherrole-table-header"
                gap={4}
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black',
                    background: 'white'
                  }
                }}
              >

                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Level
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Role
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Abbreviation
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Auto Assign
                  </Text>
                </Th>
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Max Limit
                  </Text>
                </Th>
                {isCOCRecommendationRequired && (
                  <Th p={4}>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Max Rec. Required
                    </Text>
                  </Th>
                )}
                <Th p={4} display={"none"}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Dec. Maker
                  </Text>
                </Th>
                {isCOCRecommendationRequired && (
                  <Th p={4}>
                    <Text textStyle="headingItem" whiteSpace="nowrap">
                      Recommendations
                    </Text>
                  </Th>
                )}
                <Th p={4}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Actions
                  </Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {roles.map(entry => (
                <Tr
                  key={entry.originalIndex}
                  data-testId="otherroles-table-row"
                  gap={4}
                  background="white"
                  alignItems="center"
                  _last={{ borderBottom: 'none' }}
                >

                  <Td>
                    <Text>{entry.config.roleLevel ?? '-'}</Text>
                  </Td>
                  <Td>
                    <Text textStyle="headingItem">
                      {entry.config.role || '-'}
                    </Text>
                  </Td>
                  <Td>
                    <Text>{entry.config.roleAbbreviation || '-'}</Text>
                  </Td>
                  <Td>
                    <Checkbox
                      id={`other-autoAssign-${entry.originalIndex}`}
                      name={`other-autoAssign-${entry.originalIndex}`}
                      ariaLabel={`Auto assign for ${entry.config.role}`}
                      isChecked={entry.config.autoAssign}
                      disabled
                    >
                      {' '}
                    </Checkbox>
                  </Td>
                  <Td>
                    <Text>{entry.config.roleMaxLimit ?? '-'}</Text>
                  </Td>
                  {isCOCRecommendationRequired && (
                    <Td>
                      <Text>
                        {entry.config.maxRecommendationRequiredCount ?? '-'}
                      </Text>
                    </Td>
                  )}
                  <Td display={"none"}>
                    <Checkbox
                      id={`other-isDecisionMaker-${entry.originalIndex}`}
                      name={`other-isDecisionMaker-${entry.originalIndex}`}
                      ariaLabel={`Decision maker for ${entry.config.role}`}
                      isChecked={entry.config.isDecisionMaker ?? false}
                      disabled
                    >
                      {' '}
                    </Checkbox>
                  </Td>
                  {isCOCRecommendationRequired && (
                    <Td>
                      <Flex gap={1} wrap="wrap">
                        {entry.config.decision?.length ? (
                          entry.config.decision.map(d => (
                            <Badge key={d.value} variant="interactive">
                              {d.label}
                            </Badge>
                          ))
                        ) : (
                          <Text textStyle="textContext">-</Text>
                        )}
                      </Flex>
                    </Td>
                  )}
                  <Td>
                    <Flex gap={2}>
                      <ButtonIcon
                        variant="secondary"
                        size="small"
                        ariaLabel={`Edit ${entry.config.role || 'role'}`}
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => onEdit(entry)}
                      />
                      <ButtonIcon
                        variant="secondary"
                        size="small"
                        ariaLabel={`Delete ${entry.config.role || 'role'}`}
                        icon={<FontAwesomeIcon icon={faTrash} color='red'/>}
                        onClick={() => onDelete(entry)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <OutlineCard borderless paddingX={4} paddingY={6}>
          <Flex
            flexDirection={'column'}
            gap={2}
            alignItems="center"
            paddingY={8}
          >
            <Text textStyle="headingGroup">No other roles configured</Text>
            <Text textStyle="textBody">
              Click 'Add Other Role' to add a non-owner role for this journal.
            </Text>
          </Flex>
        </OutlineCard>
      )}
    </Flex>
  );
};

export default OtherRolesSection;